import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams, Routes, Route, Navigate } from "react-router";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import Configure from "../Create/Configure";
import { bindAction, type ISegment } from "interfaces";
import { getExternalLink } from "../state/thunks/externalLinkQueriesThunk";
import { useWizardStepsManager } from "hooks/useWizardStepsManager";
import Restricted from "features/Application/Restricted";
import { PublishedStatusTypes, RolePermissions, RouteNames } from "enums";
import EditExternalLinkHeader from "./EditExternalLinkHeader";
import { Dimmer, Loader } from "semantic-ui-react";
import { ModalTypes, RestrictedResource, Segments } from "components";
import SettingsTab from "../Settings/SettingsTab";
import { resetLinkDetails } from "../state/ExternalLinkDetailsSlice";
import EditExternalLinkPeople from "../People/EditExternalLinkPeople";
import AssociatedPacks from "features/Licensing/Packs/AssociatedPacks/AssociatedPacks";
import { discardExternalLink, publishExternalLink } from "../state/thunks/externalLinkCommandThunk";
import { useRtn } from "hooks/useRtn";
import {
  LinkDiscardSuccess,
  LinkLockSuccess,
  LinkPublishSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import EditExternalLinkSubheaderContainer from "./EditExternalLinkSubheaderContainer";
import RevertConfirmationModalTs from "components/modal/RevertConfirmationModalTs";
import ModalManager from "utils/ModalManager";
import LinksPerformance from "../Performance/LinksPerformance";

type EditExternalLinkProps = PropsFromRedux;

const EditExternalLinkSteps = {
  Performance: 0,
  Configuration: 1,
  Settings: 2,
  People: 3,
  AssociatedPacks: 4,
};

const pageIndexes = [
  EditExternalLinkSteps.Performance,
  EditExternalLinkSteps.Configuration,
  EditExternalLinkSteps.Settings,
  EditExternalLinkSteps.People,
  EditExternalLinkSteps.AssociatedPacks,
];

const entityStateRtns = [LinkPublishSuccess, LinkDiscardSuccess, LinkLockSuccess];
const showLastModified = new Set([EditExternalLinkSteps.Configuration]);
const showReportLastUpdated = new Set([EditExternalLinkSteps.Performance]);

const EditExternalLinkComponent: FC<EditExternalLinkProps> = ({
  externalLink,
  isLoading,
  userId,
  getLink,
  reset,
  publishLink,
  discardLink,
}) => {
  const { id } = useParams<{ id: string }>();
  const isPublishedAndNotPurchased = !externalLink?.isDraft && !externalLink?.isPurchased;
  const [wizardPages, { goToPage, goBack }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (id) {
      getLink(Number(id));
    }
  }, [id, getLink]);

  const onEntityStateChange = useCallback(() => {
    getLink(Number(id));
  }, [getLink, id]);
  useRtn(entityStateRtns, onEntityStateChange);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const [activeTab, setActiveTab] = useState(EditExternalLinkSteps.Performance);
  const revertModalManager = useMemo(
    () => new ModalManager(ModalTypes.RevertExternalLink, () => discardLink(Number(id)), false, userId),
    [id, userId, discardLink],
  );

  const pages = useMemo<{ [key: number]: ISegment }>(() => {
    return {
      [EditExternalLinkSteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => {
          goToPage(EditExternalLinkSteps.Performance);
        },
      },
      [EditExternalLinkSteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => {
          goToPage(EditExternalLinkSteps.Configuration);
        },
      },
      [EditExternalLinkSteps.Settings]: {
        to: "settings",
        label: "Settings",
        onClick: () => {
          goToPage(EditExternalLinkSteps.Settings);
        },
      },
      [EditExternalLinkSteps.People]: {
        to: "people",
        label: "People",
        onClick: () => {
          goToPage(EditExternalLinkSteps.People);
        },
      },
      [EditExternalLinkSteps.AssociatedPacks]: {
        to: "associated-packs",
        label: "Associated Packs",
        onClick: () => {
          goToPage(EditExternalLinkSteps.AssociatedPacks);
        },
      },
    };
  }, [goToPage]);

  useLayoutEffect(() => {
    const handler = (_: any, activeStepIndex: number) => {
      pages[activeStepIndex].init?.();
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  const back = () => {
    if (!goBack()) {
      return `/${RouteNames.contentLinks}`;
    }
  };

  const handlePublish = useCallback(() => {
    if (!externalLink || !externalLink.isDraft) {
      return;
    }

    publishLink(externalLink.id);
  }, [publishLink, externalLink]);

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content scrollable-content">
          <EditExternalLinkHeader
            title={externalLink?.title || ""}
            back={back}
            canBeEdited={!externalLink?.isPurchased && !externalLink?.isDraft}
            canBePublished={!!externalLink?.isDraft}
            isRevertVisible={externalLink.hasBeenPublished}
            crudPermission={hasPermission}
            invalidFormDetails={!isValid}
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(
              !externalLink?.isDraft,
              externalLink?.hasBeenPublished,
            )}
            hideEntityStateButtons={
              activeTab === EditExternalLinkSteps.People || activeTab === EditExternalLinkSteps.Performance
            }
            entityStateActions={{
              onRevert: () => revertModalManager.execute(),
              onPublish: handlePublish,
            }}
            canAddPacks={isPublishedAndNotPurchased}
            showAddPacksButton={activeTab === EditExternalLinkSteps.AssociatedPacks}
          />
          {externalLink.hasBeenPublished && <RevertConfirmationModalTs modalManager={revertModalManager} />}
          <Dimmer active={isLoading} inverted>
            <Loader />
          </Dimmer>
          <EditExternalLinkSubheaderContainer
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(
              !!externalLink?.isDraft,
              externalLink?.hasBeenPublished,
            )}
            isUpdateInProgress={!!externalLink?.isDraft}
            lastModifiedDateTime={externalLink?.dateModified}
            showLastModified={showLastModified.has(activeTab)}
            showReportLastUpdated={showReportLastUpdated.has(activeTab)}
          >
            <Segments to={`/${RouteNames.contentLinks}/${id}`}>
              <Segments.Segment {...pages[EditExternalLinkSteps.Performance]} />
              <Segments.Segment {...pages[EditExternalLinkSteps.Configuration]} />
              <Segments.Segment {...pages[EditExternalLinkSteps.Settings]} />
              <Segments.Segment {...pages[EditExternalLinkSteps.People]} />
              <Segments.Segment {...pages[EditExternalLinkSteps.AssociatedPacks]} />
            </Segments>
          </EditExternalLinkSubheaderContainer>
          <div className="scrollable-content">
            <Routes>
              <Route
                path={pages[EditExternalLinkSteps.Performance].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <LinksPerformance linkId={Number(id)} linkName={externalLink.title} />
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditExternalLinkSteps.Configuration].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <Configure
                        acceptHandlers={wizardPages[EditExternalLinkSteps.Configuration]}
                        disabled={!externalLink?.isDraft}
                        onIsValidChange={setIsValid}
                      />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditExternalLinkSteps.Settings].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <SettingsTab acceptHandlers={wizardPages[EditExternalLinkSteps.Settings]} />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditExternalLinkSteps.People].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <EditExternalLinkPeople
                      linkId={Number(id)}
                      acceptHandlers={wizardPages[EditExternalLinkSteps.People]}
                    />
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditExternalLinkSteps.AssociatedPacks].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <AssociatedPacks
                      acceptHandlers={wizardPages[EditExternalLinkSteps.AssociatedPacks]}
                      contentType="Link"
                      contentId={Number(id)}
                      canAddPacks={isPublishedAndNotPurchased}
                      permissions={[RolePermissions.AssetsCreate]}
                    />
                  </RestrictedResource>
                }
              />
              <Route path="*" element={<Navigate to={pages[EditExternalLinkSteps.Performance].to!} />} />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  const externalLinks = state.library.externalLinks.details;
  return {
    externalLink: externalLinks.link,
    error: externalLinks.error,
    isLoading: externalLinks.isLoading,
    isLoaded: externalLinks.isLoaded,
    userId: Number(state.userProfile.id),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getLink: bindAction(getExternalLink, dispatch),
  reset: bindAction(resetLinkDetails, dispatch),
  publishLink: bindAction(publishExternalLink, dispatch),
  discardLink: bindAction(discardExternalLink, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditExternalLinkComponent);

export default ConnectedComponent;
